import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import ConfirmModal from "components/common/ConfirmModal";
import {
  MdMoreVert,
  MdOutlineRemoveRedEye,
  MdOutlineEdit,
  MdDelete,
} from "react-icons/md";
import { getImageUrl } from "utils/imageHelper";
import Toast from "components/Toast";

export default function SpeakerTable({
  data,
  onDelete,
  onRefresh,
  selectedRows = [],
  onSelectRow,
  onSelectAll,
  viewButton,
  fetchData
}) {
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const [toast, setToast] = useState({
    open: false,
    title: "",
    message: "",
    type: "success",
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggleMenu = (e, rowId) => {
    e.stopPropagation();
    if (openDropdownId === rowId) {
      setOpenDropdownId(null);
    } else {
      const buttonRect = e.currentTarget.getBoundingClientRect();
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      setOpenUpward(spaceBelow < 150);
      setOpenDropdownId(rowId);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await onDelete(deleteId);
      setToast({
        open: true,
        title: "Success",
        message: "Speaker deleted successfully",
        type: "success",
      });
      setDeleteId(null);
      fetchData();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="table-scroll-body">
        <table className="custom-table">
          <thead className="custom-table-header text-gray-800">
            <tr>
              <th className="w-12 text-center">
                <input
                  type="checkbox"
                  className="table-checkbox"
                  onChange={onSelectAll}
                  checked={
                    data.length > 0 && selectedRows.length === data.length
                  }
                />
              </th>
              <th>Profile</th>
              <th>Name</th>
              <th>Email</th>
              <th>Profession</th>
              <th>Website</th>
              <th className="w-16 text-center"></th>
            </tr>
          </thead>

          <tbody>
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan="7"
                  className="custom-table-cell py-12 text-center text-gray-500"
                >
                  No speakers found
                </td>
              </tr>
            ) : (
              data.map((item, index) => {
                const rowId = item._id || index;
                const isChecked = selectedRows.includes(rowId);
                const isMenuOpen = openDropdownId === rowId;

                return (
                  <tr key={rowId} className="custom-table-row">
                    <td className="custom-table-cell text-center">
                      <input
                        type="checkbox"
                        className="table-checkbox"
                        checked={isChecked}
                        onChange={() => onSelectRow(rowId)}
                      />
                    </td>

                    <td className="custom-table-cell">
                      {item.profileImage ? (
                        <img
                          src={getImageUrl(item.profileImage)}
                          alt={item.name}
                          className="h-10 w-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-500">
                          N/A
                        </div>
                      )}
                    </td>

                    <td className="custom-table-cell ">
                      {item.name || "N/A"}
                    </td>

                    <td className="custom-table-cell ">
                      {item.email || "N/A"}
                    </td>

                    <td className="custom-table-cell ">
                      {item.profession || "N/A"}
                    </td>

                    <td className="custom-table-cell text-blue-600 underline">
                      {item.website ? (
                        <a
                          href={item.website}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {item.website}
                        </a>
                      ) : (
                        "N/A"
                      )}
                    </td>

                    <td className="custom-table-cell relative text-center">
                      <button
                        onClick={(e) => handleToggleMenu(e, rowId)}
                        className="action-menu-btn"
                      >
                        <MdMoreVert className="text-lg text-gray-800" />
                      </button>

                      {isMenuOpen && (
                        <div
                          ref={dropdownRef}
                          className={`action-dropdown-menu ${
                            openUpward ? "menu-bottom" : "menu-top"
                          }`}
                        >
                          <button
                            onClick={() => {
                              setOpenDropdownId(null);
                              viewButton(item);
                            }}
                            className="action-dropdown-item"
                          >
                            <MdOutlineRemoveRedEye className="text-base" />
                            View
                          </button>

                          <div className="action-dropdown-divider" />

                          <Link
                            to={`/admin/speakers/edit/${item._id}`}
                            onClick={() => setOpenDropdownId(null)}
                            className="action-dropdown-item"
                          >
                            <MdOutlineEdit className="text-base" />
                            Edit
                          </Link>

                          <div className="action-dropdown-divider" />

                          <button
                            onClick={() => {
                              setOpenDropdownId(null);
                              setDeleteId(item._id);
                            }}
                            className="action-dropdown-item item-danger"
                          >
                            <MdDelete className="text-base" />
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        open={Boolean(deleteId)}
        title="Are you sure?"
        message="You are about to delete this speaker profile. This action cannot be undone."
        confirmText="Yes, Delete"
        loading={loading}
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
      <Toast
        open={toast.open}
        title={toast.title}
        message={toast.message}
        type={toast.type}
        duration={1500}
        onClose={() => {
          setToast((prev) => ({ ...prev, open: false }));
        }}
      />
    </>
  );
}